import { Loader, Stack, Text, Center, Box, Skeleton, Container } from '@mantine/core';
import styles from './LoadingState.module.css';

export interface LoadingStateProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  message?: string;
  variant?: 'dots' | 'oval' | 'bars';
  overlay?: boolean;
  fullHeight?: boolean;
  showSkeleton?: boolean;
  skeletonType?: 'conversation' | 'list' | 'agents' | 'default';
}

export function LoadingState({ 
  size = 'md', 
  message, 
  variant = 'dots',
  overlay = false,
  fullHeight = false,
  showSkeleton = false,
  skeletonType = 'default'
}: LoadingStateProps) {
  // Return skeleton loading for specific types
  if (showSkeleton) {
    if (skeletonType === 'conversation') {
      return (
        <Box 
          w="100%" 
          h="100%" 
          style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '100%'
          }}
        >
          <Box maw={1040} w="100%" mx="auto" p="36px 18px" style={{ flex: 1 }}>
            <Stack gap="md" justify="flex-start">
              {Array.from({ length: 8 }).map((_, i) => (
                <Box key={i} w="100%" className={`${styles.skeletonItem}`}>
                  <Skeleton height={80} radius="md" w="100%" />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      );
    }
    
    if (skeletonType === 'list') {
      return (
        <Stack gap="sm" p="md">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={40} radius="sm" />
          ))}
        </Stack>
      );
    }

    if (skeletonType === 'agents') {
      // Grid-style skeleton aligned with Agents page layout
      return (
        <Box className={styles.agentsSkeletonShell}>
          <Container size="xl" className={styles.agentsSkeletonContainer}>
            <Stack gap={42} align="center" className={styles.agentsSkeletonStack}>
              {/* Title */}
              <Skeleton height={32} width={120} radius="sm" />

              <Box className={styles.agentsSkeletonSearch}>
                <Skeleton height={40} radius={6} />
              </Box>

              {/* Pinned section */}
              <Stack gap={18} align="center" className={styles.agentsSkeletonSection}>
                <Skeleton height={20} width={70} radius="sm" />
                <Box className={styles.agentsSkeletonGrid}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Box key={`pinned-${i}`} className={styles.agentsSkeletonCard}>
                      <Skeleton height={120} radius={6} />
                    </Box>
                  ))}
                </Box>
              </Stack>

              {/* Recommended section */}
              <Stack gap={18} align="center" className={styles.agentsSkeletonSection}>
                <Skeleton height={20} width={120} radius="sm" />
                <Box className={styles.agentsSkeletonGrid}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Box key={`rec-${i}`} className={styles.agentsSkeletonCard}>
                      <Skeleton height={120} radius={6} />
                    </Box>
                  ))}
                </Box>
              </Stack>
            </Stack>
          </Container>
        </Box>
      );
    }
  }

  const content = (
    <Stack 
      align="center" 
      gap="md"
      classNames={{
        root: styles.loadingContainer
      }}
    >
      <Loader 
        size={size} 
        type={variant}
        classNames={{
          root: styles.loader
        }}
      />
      {message && (
        <Text 
          size="sm" 
          c="dimmed" 
          ta="center"
          classNames={{
            root: styles.loadingMessage
          }}
        >
          {message}
        </Text>
      )}
    </Stack>
  );

  if (overlay) {
    return (
      <div className={styles.overlay}>
        <Center h="100%">
          {content}
        </Center>
      </div>
    );
  }

  if (fullHeight) {
    return (
      <Center 
        h="100vh"
        classNames={{
          root: styles.fullHeight
        }}
      >
        {content}
      </Center>
    );
  }

  return content;
}

// Loading skeleton for better UX
export interface LoadingSkeletonProps {
  lines?: number;
  height?: number;
  animate?: boolean;
}

export function LoadingSkeleton({ 
  lines = 3, 
  height = 20, 
  animate = true 
}: LoadingSkeletonProps) {
  return (
    <Stack gap="sm">
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className={[
            styles.skeletonLine,
            animate && styles.skeletonAnimate
          ].filter(Boolean).join(' ')}
          style={{ 
            height: `${height}px`,
            width: index === lines - 1 ? '70%' : '100%'
          }}
        />
      ))}
    </Stack>
  );
}
